import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const PRIORITY_META = {
  none:   { label:'—',      color:'#94A3B8', bg:'#F8FAFC' },
  low:    { label:'Low',    color:'#64748B', bg:'#F8FAFC' },
  medium: { label:'Medium', color:'#D97706', bg:'#FFFBEB' },
  high:   { label:'High',   color:'#EA580C', bg:'#FFF7ED' },
  urgent: { label:'Urgent', color:'#DC2626', bg:'#FEF2F2' },
};
const STATUS_META = {
  backlog:     { label:'Backlog',      color:'#94A3B8' },
  todo:        { label:'To Do',        color:'#073E81' },
  in_progress: { label:'In Progress',  color:'#D97706' },
  review:      { label:'In Review',    color:'#7C3AED' },
  done:        { label:'Done',         color:'#059669' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short'});
}
function daysDue(d) {
  if (!d) return null;
  return Math.ceil((new Date(d)-new Date())/86400000);
}

export default function MyTasksPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'My';
  const [filterStatus, setFilterStatus] = useState('active'); // active | done | all
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');

  const { data: tasks=[], isLoading } = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.get('/tasks').then(r=>r.data),
  });

  const filtered = tasks.filter(t => {
    if (filterStatus === 'active' && t.status === 'done') return false;
    if (filterStatus === 'done'   && t.status !== 'done') return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !(t.project_name||'').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by due status
  const overdue  = filtered.filter(t => { const d=daysDue(t.due_date); return d!==null&&d<0&&t.status!=='done'; });
  const dueToday = filtered.filter(t => { const d=daysDue(t.due_date); return d!==null&&d===0&&t.status!=='done'; });
  const upcoming = filtered.filter(t => { const d=daysDue(t.due_date); return (d===null||d>0)&&t.status!=='done'; });
  const done     = filtered.filter(t => t.status==='done');

  async function markDone(task) {
    try {
      await api.patch(`/tasks/${task.id}`, { status: task.status==='done'?'todo':'done' });
      qc.invalidateQueries(['my-tasks']);
    } catch { toast.error('Update failed'); }
  }

  function TaskRow({ task }) {
    const pri  = PRIORITY_META[task.priority]||PRIORITY_META.none;
    const st   = STATUS_META[task.status]||STATUS_META.todo;
    const days = daysDue(task.due_date);
    const isOver = days!==null&&days<0&&task.status!=='done';

    return (
      <div style={{
        display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
        borderBottom:'1px solid var(--border)', background:'var(--surface)',
        transition:'background .1s',
      }}
        onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
        onMouseLeave={e=>e.currentTarget.style.background='var(--surface)'}
      >
        {/* Checkbox */}
        <input type="checkbox" checked={task.status==='done'} onChange={()=>markDone(task)}
          style={{ width:16, height:16, cursor:'pointer', flexShrink:0 }} />

        {/* Project color dot */}
        <div style={{ width:8, height:8, borderRadius:'50%', background:task.project_color||'#073E81', flexShrink:0 }} />

        {/* Title + project */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:13,
            textDecoration:task.status==='done'?'line-through':'none',
            color:task.status==='done'?'var(--text3)':'var(--text1)',
            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {task.title}
          </div>
          <div style={{ fontSize:11, color:'var(--text3)', marginTop:1 }}>
            <span style={{ cursor:'pointer', textDecoration:'underline dotted' }}
              onClick={()=>navigate(`/projects/${task.project_id}`)}>
              {task.project_name}
            </span>
          </div>
        </div>

        {/* Priority */}
        <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
          background:pri.bg, color:pri.color, whiteSpace:'nowrap' }}>
          {pri.label||'—'}
        </span>

        {/* Status */}
        <span style={{ fontSize:10, fontWeight:700, color:st.color, whiteSpace:'nowrap' }}>
          {st.label}
        </span>

        {/* Due date */}
        <span style={{ fontSize:11, fontWeight: isOver?700:400, color:isOver?'#DC2626':days===0?'#D97706':'var(--text3)', whiteSpace:'nowrap', minWidth:70, textAlign:'right' }}>
          {task.due_date ? fmtDate(task.due_date) : '—'}
          {isOver && ' ⚠'}
        </span>

        {/* Open */}
        <button onClick={()=>navigate(`/projects/${task.project_id}`)}
          style={{ fontSize:11, color:'var(--text3)', background:'none', border:'none', cursor:'pointer', padding:'2px 6px', borderRadius:4 }}
          title="Open project">
          →
        </button>
      </div>
    );
  }

  function Section({ label, tasks, color }) {
    if (tasks.length === 0) return null;
    return (
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, padding:'6px 16px',
          background:'var(--surface2)', borderRadius:8, borderLeft:`3px solid ${color}` }}>
          <span style={{ fontSize:12, fontWeight:700, color }}>{label}</span>
          <span style={{ fontSize:11, color:'var(--text3)' }}>{tasks.length} task{tasks.length!==1?'s':''}</span>
        </div>
        <div style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
          {tasks.map(t=><TaskRow key={t.id} task={t}/>)}
        </div>
      </div>
    );
  }

  const counts = {
    active: tasks.filter(t=>t.status!=='done').length,
    done:   tasks.filter(t=>t.status==='done').length,
    overdue: overdue.length,
    urgent: tasks.filter(t=>t.status!=='done'&&(t.priority==='urgent'||t.priority==='high')).length,
  };
  const total = tasks.length;
  const completionPct = total ? Math.round((counts.done/total)*100) : 0;

  const statusCounts = Object.fromEntries(
    Object.keys(STATUS_META).map(s=>[s, tasks.filter(t=>t.status===s).length])
  );

  const initials = (user?.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div>
      <div className="section-head">
        <h2>{firstName}'s Tasks</h2>
      </div>

      {/* Overview card — mirrors owner's team view */}
      <div className="card" style={{ marginBottom:20, padding:'20px 24px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16 }}>
          <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--accent)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, flexShrink:0 }}>
            {initials}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15 }}>{user?.name}</div>
            <div style={{ fontSize:12, color:'var(--text3)', textTransform:'capitalize' }}>{user?.role}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:26, fontWeight:800, color:'var(--accent)' }}>{total}</div>
            <div style={{ fontSize:11, color:'var(--text3)' }}>tasks</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text3)', marginBottom:4 }}>
            <span>COMPLETION</span><span>{completionPct}%</span>
          </div>
          <div style={{ height:6, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${completionPct}%`, background:'var(--accent)', borderRadius:4, transition:'width .4s' }}/>
          </div>
        </div>

        {/* Status pills */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:counts.overdue>0||counts.urgent>0?12:0 }}>
          {Object.entries(STATUS_META).map(([k,v])=> statusCounts[k]>0 && (
            <span key={k} style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:10, background:'var(--surface2)', color:v.color }}>
              {v.label} {statusCounts[k]}
            </span>
          ))}
        </div>

        {/* Alerts */}
        {(counts.overdue>0||counts.urgent>0) && (
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {counts.overdue>0 && <span style={{ fontSize:11, fontWeight:700, color:'#DC2626' }}>▲ {counts.overdue} overdue</span>}
            {counts.urgent>0 && <span style={{ fontSize:11, fontWeight:700, color:'#EA580C' }}>🔥 {counts.urgent} urgent/high</span>}
          </div>
        )}
      </div>

      {/* Header stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:24 }}>
        {[
          ['Active Tasks', counts.active, '#073E81'],
          ['Overdue',      counts.overdue, '#DC2626'],
          ['Completed',    counts.done,   '#059669'],
        ].map(([label,val,color])=>(
          <div key={label} className="card" style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ fontSize:28, fontWeight:800, color }}>{val}</div>
            <div style={{ fontSize:12, color:'var(--text2)' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <input className="form-input" placeholder="Search tasks…" value={search}
          onChange={e=>setSearch(e.target.value)} style={{ width:200 }} />

        <div style={{ display:'flex', gap:4 }}>
          {[['active','Active'],['done','Done'],['all','All']].map(([k,label])=>(
            <button key={k} onClick={()=>setFilterStatus(k)} style={{
              padding:'5px 12px', borderRadius:6, fontSize:12, border:'1px solid var(--border)',
              background:filterStatus===k?'var(--accent)':'var(--surface)',
              color:filterStatus===k?'#fff':'var(--text2)', cursor:'pointer', fontWeight:filterStatus===k?700:400,
            }}>{label}</button>
          ))}
        </div>

        <select className="form-input" value={filterPriority} onChange={e=>setFilterPriority(e.target.value)} style={{ width:130 }}>
          <option value="">All priorities</option>
          {Object.entries(PRIORITY_META).filter(([k])=>k!=='none').map(([k,v])=>(
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <div className="loading-screen"><div className="spinner"/></div>}

      {!isLoading && filtered.length === 0 && (
        <div className="card" style={{ padding:60, textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
          <div style={{ fontWeight:700 }}>You're all caught up!</div>
          <div style={{ color:'var(--text2)', marginTop:6 }}>No tasks match this filter.</div>
        </div>
      )}

      {filterStatus !== 'done' && <>
        <Section label="⚠ Overdue"  tasks={overdue}  color="#DC2626" />
        <Section label="📅 Due Today" tasks={dueToday} color="#D97706" />
        <Section label="📋 Upcoming" tasks={upcoming} color="#073E81" />
      </>}
      {filterStatus === 'done' && <Section label="✅ Completed" tasks={done} color="#059669" />}
      {filterStatus === 'all'  && <>
        <Section label="⚠ Overdue"    tasks={overdue}  color="#DC2626" />
        <Section label="📅 Due Today" tasks={dueToday} color="#D97706" />
        <Section label="📋 Upcoming"  tasks={upcoming} color="#073E81" />
        <Section label="✅ Done"      tasks={done}     color="#059669" />
      </>}
    </div>
  );
}
